import { describeBatteryPricingError } from "~/api/battery-pricing-errors"
import type {
  BatteryPricingStatus,
  ProposalCostDto,
  ProposalCostLineItemDto,
} from "~/api/dto/battery-proposal.dto"
import useBatteryProposal from "~/api/hooks/useBatteryProposal"
import useBatteryProposalPricing from "~/api/hooks/useBatteryProposalPricing"
import useCalculateBatteryProposalPricing from "~/api/hooks/useCalculateBatteryProposalPricing"
import useUpdateBatteryProposalDetails from "~/api/hooks/useUpdateBatteryProposalDetails"
import { Drawer } from "~/components/ui-components/v2"
import { formatCurrency } from "~/utils/finance/format"
import {
  costOfLineItem,
  PricingSummaryLineItemType,
} from "~/utils/finance/pricing-summary"

import { previewTotalCostForMargin } from "../../ProposalPresentation/pricing/preview-total-cost-for-margin"
import { describePricingError } from "../../ProposalPresentation/pricing/refresh-pricing-action"
import {
  PricingSetupGapLeadLink,
  PricingSetupGaps,
  SaveOrRefreshPricingFooter,
  type PricingSetupGapLink,
} from "../../ProposalPresentation/pricing/system-pricing-actions"
import {
  MarginField,
  useMarginDraft,
  type MarginDraftRules,
} from "../../ProposalPresentation/pricing/system-pricing-margin"
import {
  PricingBreakdownTable,
  PricingTotals,
  type PricingBreakdownGroup,
} from "../../ProposalPresentation/pricing/system-pricing-table"
import { FinancingLockNotice } from "../financing-status/concert/FinancingLockNotice"
import { useBatteryFinancingLock } from "../financing-status/concert/use-battery-financing-lock"
import { PricingOutOfDateNotice } from "../pricing-out-of-date"
import {
  batteryPricingSetupGaps,
  isBatteryPricingSetupComplete,
  type BatteryPricingSetupGap,
} from "./battery-pricing-setup-gaps"
import { batterySetupStepPath, SETUP_STEP_LABELS } from "./setup-steps"

export { isBatteryPricingSetupComplete } from "./battery-pricing-setup-gaps"

const FOOTER_LINE_TYPES = new Set<string>([
  PricingSummaryLineItemType.MARGIN,
  PricingSummaryLineItemType.SALES_TAX,
  PricingSummaryLineItemType.GENERAL_EXCISE_TAX,
])

const EQUIPMENT_LINE_TYPES = new Set<string>([
  PricingSummaryLineItemType.MODULE,
  PricingSummaryLineItemType.INVERTER,
  PricingSummaryLineItemType.ENERGY_BACKUP,
  PricingSummaryLineItemType.ADDITIONAL_EQUIPMENT,
  PricingSummaryLineItemType.OPTIMIZER,
  PricingSummaryLineItemType.EQUIPMENT,
  PricingSummaryLineItemType.EV_OUTLET,
  PricingSummaryLineItemType.EV_CHARGER,
])
const FEE_LINE_TYPES = new Set<string>([
  PricingSummaryLineItemType.FEE,
  PricingSummaryLineItemType.DEALER_FEE,
  PricingSummaryLineItemType.POWUR_LENDER_MARGIN_FEE,
])
const CREDIT_LINE_TYPES = new Set<string>([
  PricingSummaryLineItemType.INCENTIVE,
  PricingSummaryLineItemType.REBATE,
  PricingSummaryLineItemType.DISCOUNT,
])

type LineGroupKey = `adders` | `equipment` | `fees`

const LINE_GROUP_ORDER: LineGroupKey[] = [`equipment`, `adders`, `fees`]
const LINE_GROUP_LABEL: Record<LineGroupKey, string> = {
  equipment: `Equipment`,
  adders: `Additional Items`,
  fees: `Fees`,
}

const lineGroupOf = (item: ProposalCostLineItemDto): LineGroupKey => {
  if (EQUIPMENT_LINE_TYPES.has(item.type)) return `equipment`
  if (FEE_LINE_TYPES.has(item.type)) return `fees`
  return `adders`
}

type LineGroups = Record<LineGroupKey, ProposalCostLineItemDto[]>

const groupLineItems = (pricing: ProposalCostDto): LineGroups => {
  const groups: LineGroups = { equipment: [], adders: [], fees: [] }
  for (const item of pricing.lineItems ?? []) {
    if (FOOTER_LINE_TYPES.has(item.type) || CREDIT_LINE_TYPES.has(item.type)) {
      continue
    }
    groups[lineGroupOf(item)].push(item)
  }
  return groups
}

/** Battery's grouping of its stored line items, as the shared table's groups. */
const batteryPricingBreakdownGroups = (
  pricing: ProposalCostDto,
): PricingBreakdownGroup[] => {
  const groups = groupLineItems(pricing)
  return LINE_GROUP_ORDER.filter((key) => groups[key].length > 0).map((key) => ({
    key,
    label: LINE_GROUP_LABEL[key],
    rows: groups[key].map((item) => ({
      key: item.id,
      label: item.displayName,
      amount: costOfLineItem(item),
      ppw: null,
      namesOnly: false,
    })),
  }))
}

const gapActionLabel = (gap: BatteryPricingSetupGap): string | null => {
  if (gap.step == null) {
    return null
  }
  if (gap.id === `utility-company`) {
    return `Open Utility`
  }
  return `Open ${SETUP_STEP_LABELS[gap.step]}`
}

const toGapLink = (
  proposalId: string,
  gap: BatteryPricingSetupGap,
): PricingSetupGapLink => ({
  key: gap.id,
  label: gap.label,
  href: gap.step != null ? batterySetupStepPath(proposalId, gap.step) : null,
  action: gapActionLabel(gap),
})

const BatteryPricingSetupGaps = ({
  proposalId,
  gaps,
}: {
  proposalId: string
  gaps: BatteryPricingSetupGap[]
}): JSX.Element => {
  const utilityGap = gaps.find((gap) => gap.id === `utility-company`)
  const otherGaps = gaps.filter((gap) => gap.id !== `utility-company`)

  return (
    <PricingSetupGaps
      gaps={otherGaps.map((gap) => toGapLink(proposalId, gap))}
      lead={
        utilityGap != null ? (
          <p>
            Utility setup is required before pricing can be calculated.
            {utilityGap.step != null ? (
              <>
                {` `}
                <PricingSetupGapLeadLink
                  href={batterySetupStepPath(proposalId, utilityGap.step)}
                >
                  {gapActionLabel(utilityGap)}
                </PricingSetupGapLeadLink>
              </>
            ) : null}
          </p>
        ) : null
      }
    />
  )
}

const REQUESTED_FIXED_MARGIN_ERROR = `Enter a dollar amount greater than zero with at most two decimal places.`

const formatRequestedFixedMarginDraft = (amount: number): string => {
  if (!Number.isFinite(amount)) {
    return ``
  }
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2)
}

/** Whole cents above zero; one message covers every way a draft fails. */
const REQUESTED_FIXED_MARGIN_RULES: MarginDraftRules = {
  decimals: 2,
  min: 0.01,
  max: null,
  messages: {
    invalid: REQUESTED_FIXED_MARGIN_ERROR,
    tooLow: REQUESTED_FIXED_MARGIN_ERROR,
    tooHigh: REQUESTED_FIXED_MARGIN_ERROR,
  },
  format: formatRequestedFixedMarginDraft,
}

const marginLineCost = (pricing: ProposalCostDto): number =>
  pricing.lineItems.find(
    (item) => item.type === PricingSummaryLineItemType.MARGIN,
  )?.cost ?? 0

const StoredSystemPricing = ({
  proposalId,
  pricing,
  pricingStatus,
  requestedFixedMargin,
  canEdit,
  onClose,
}: {
  proposalId: string
  pricing: ProposalCostDto
  pricingStatus: BatteryPricingStatus
  requestedFixedMargin: number
  canEdit: boolean
  onClose: () => void
}): JSX.Element => {
  const margin = useMarginDraft({
    saved: requestedFixedMargin,
    seedKey: `${pricing.id}:${requestedFixedMargin}`,
    rules: REQUESTED_FIXED_MARGIN_RULES,
  })
  const saveMargin = useUpdateBatteryProposalDetails()
  const refreshPricing = useCalculateBatteryProposalPricing()

  const isSavingOrRefreshing = saveMargin.isLoading || refreshPricing.isLoading
  const embeddedMargin = marginLineCost(pricing)
  const previewTotalCost = previewTotalCostForMargin({
    totalCost: pricing.totalCost,
    pricedMargin: embeddedMargin,
    draftMargin: margin.parsed,
    multiplier: 1,
  })

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col">
        <PricingBreakdownTable groups={batteryPricingBreakdownGroups(pricing)} />
        <PricingTotals
          margin={
            canEdit ? (
              <MarginField
                unit="$"
                draft={margin.draft}
                error={margin.error}
                disabled={isSavingOrRefreshing}
                onChange={margin.setDraft}
              />
            ) : (
              formatCurrency(embeddedMargin)
            )
          }
          salesTax={pricing.salesTax}
          exciseTax={pricing.exciseTax !== 0 ? pricing.exciseTax : null}
          totalCost={previewTotalCost}
        />
      </div>
      <SaveOrRefreshPricingFooter
        save={saveMargin}
        // Save + refresh run inside the details mutation so the pricing POST
        // still fires if this drawer unmounts mid-save.
        saveVariables={
          margin.parsed != null
            ? { proposalId, body: { requestedFixedMargin: margin.parsed } }
            : null
        }
        refresh={refreshPricing}
        refreshVariables={{ proposalId }}
        describeRefreshError={(error) =>
          describeBatteryPricingError(error, describePricingError(error))
        }
        dirty={margin.dirty}
        canEdit={canEdit}
        outOfDateNotice={
          pricingStatus === `out_of_date` ? <PricingOutOfDateNotice /> : null
        }
        onClose={onClose}
      />
    </>
  )
}

export const SystemPricingStep = ({
  proposalId,
  onClose,
}: {
  proposalId: string
  onClose: () => void
}): JSX.Element => {
  const proposalQuery = useBatteryProposal(proposalId)
  const pricingQuery = useBatteryProposalPricing(proposalId)
  const calculatePricing = useCalculateBatteryProposalPricing()
  const financingLock = useBatteryFinancingLock(proposalId)
  const canEditPricing = !financingLock.locked && !financingLock.pending
  const pricing = pricingQuery.data?.pricing ?? null
  const setupGaps =
    proposalQuery.data != null ? batteryPricingSetupGaps(proposalQuery.data) : []
  const setupComplete = isBatteryPricingSetupComplete(proposalQuery.data)
  const calculateDisabled =
    !setupComplete ||
    !canEditPricing ||
    calculatePricing.isLoading ||
    proposalQuery.isLoading

  return (
    <Drawer
      open
      theme="dark"
      onClose={onClose}
      title="System Pricing"
      // Unprefixed max-w-[50vw] capped the drawer at half the viewport on
      // phones too; below sm it must stretch like the other setup drawers.
      widthClass="sm:max-w-[50vw]"
      contentClassName="flex min-h-0 flex-col overflow-hidden"
    >
      <FinancingLockNotice proposalId={proposalId} lock={financingLock} />
      {pricingQuery.isLoading ? (
        <p className="text-[length:var(--step--1)] text-muted">
          Loading pricing
        </p>
      ) : null}
      {pricingQuery.isError ? (
        <div className="flex flex-col gap-2">
          <p role="alert" className="text-[length:var(--step--2)] text-danger">
            Unable to load stored pricing
          </p>
          <button
            type="button"
            onClick={() => {
              void pricingQuery.refetch()
            }}
            className="self-start rounded-md border border-border-strong px-3 py-1.5 text-[length:var(--step--2)] font-medium text-foreground"
          >
            Retry
          </button>
        </div>
      ) : null}

      {pricingQuery.isSuccess && pricing == null ? (
        <div className="flex flex-col gap-3">
          <p role="status" className="text-[length:var(--step--1)] text-muted">
            No stored pricing
          </p>
          {setupGaps.length > 0 ? (
            <BatteryPricingSetupGaps proposalId={proposalId} gaps={setupGaps} />
          ) : null}
          {calculatePricing.isError ? (
            <p role="alert" className="text-[length:var(--step--2)] text-danger">
              {describeBatteryPricingError(
                calculatePricing.error,
                `Unable to calculate pricing`,
              )}
            </p>
          ) : null}
          <button
            type="button"
            disabled={calculateDisabled}
            onClick={() => {
              calculatePricing.mutate({ proposalId })
            }}
            className="self-start rounded-md bg-bp-accent px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-on-accent hover:bg-bp-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {calculatePricing.isLoading ? `Calculating` : `Calculate Pricing`}
          </button>
        </div>
      ) : null}

      {pricingQuery.isSuccess &&
      pricing != null &&
      proposalQuery.data != null ? (
        <StoredSystemPricing
          proposalId={proposalId}
          pricing={pricing}
          pricingStatus={pricingQuery.data?.pricingStatus ?? `out_of_date`}
          requestedFixedMargin={proposalQuery.data.requestedFixedMargin}
          canEdit={canEditPricing}
          onClose={onClose}
        />
      ) : (
        <div className="mt-6 flex shrink-0 items-center border-t border-border pt-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border-strong px-4 py-2 text-[length:var(--step--1)] font-medium text-foreground"
          >
            Close
          </button>
        </div>
      )}
    </Drawer>
  )
}
